import { Injectable, inject, signal } from '@angular/core';
import { FirebaseRemoteConfig } from '@capacitor-firebase/remote-config';
import { ENVIRONMENT } from '@ld.consumer-portal/models';
import { LoggingService } from './logging.service';
import { PlatformService } from './platform.service';

export interface CookieConsentConfig {
  enabled: boolean;
  frequency_unit: string | null;
  frequency_value: number | null;
}

export interface RemoteConfigValues {
  min_app_version_ios: string;
  min_app_version_android: string;
  force_update_enabled: boolean;
  force_update_title: string;
  force_update_message: string;
  maintenance_mode: boolean;
  maintenance_message: string;
  maintenance_message_web: string;
  ota_updates_enabled: boolean;
  ota_manifest_url: string;
  use_apim_customer_identity: boolean;
  show_origination: boolean;
  cookie_consent: CookieConsentConfig;
}

export interface AppConfigJson {
  development?: RemoteConfigValues;
  qa?: RemoteConfigValues;
  uat?: RemoteConfigValues;
  production?: RemoteConfigValues;
}

export type AppConfigEnvironment = 'development' | 'qa' | 'uat' | 'production';

const DEFAULT_CONFIG: RemoteConfigValues = {
  min_app_version_ios: '1.0.0',
  min_app_version_android: '1.0.0',
  force_update_enabled: false,
  force_update_title: 'Update Required',
  force_update_message: 'Please update to the latest version to continue using the app.',
  maintenance_mode: false,
  maintenance_message: 'The app is currently under maintenance. Please try again later.',
  maintenance_message_web: 'The website is currently under maintenance. Please try again later.',
  ota_updates_enabled: true,
  ota_manifest_url: '',
  use_apim_customer_identity: true,
  show_origination: false,
  cookie_consent: {
    enabled: true,
    frequency_unit: null,
    frequency_value: null,
  },
};

const HOST_ENV_TO_CONFIG_KEY: Record<string, AppConfigEnvironment> = {
  LOCAL: 'development',
  DV1: 'development',
  QA1: 'qa',
  SG1: 'uat',
  PROD: 'production',
};

@Injectable({ providedIn: 'root' })
export class RemoteConfigService {
  private readonly platformService = inject(PlatformService);
  private readonly env = inject(ENVIRONMENT);
  private readonly loggingService = inject(LoggingService);

  readonly isInitialized = signal(false);

  readonly isLoading = signal(false);

  readonly config = signal<RemoteConfigValues>(DEFAULT_CONFIG);

  readonly error = signal<string | null>(null);

  async initialize(): Promise<void> {
    if (!this.platformService.isNativeMobile()) {
      this.isInitialized.set(true);
      return;
    }

    this.isLoading.set(true);
    this.error.set(null);

    try {
      await FirebaseRemoteConfig.setSettings({
        minimumFetchIntervalInSeconds: 10,
      });

      await FirebaseRemoteConfig.fetchAndActivate();

      await this.loadConfigValues();

      this.isInitialized.set(true);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch remote config';
      this.loggingService.logException(err, 'RemoteConfigService: Initialization failed');
      this.error.set(errorMessage);
      this.isInitialized.set(true);
    } finally {
      this.isLoading.set(false);
    }
  }

  async refresh(): Promise<void> {
    if (!this.platformService.isNativeMobile()) {
      return;
    }

    this.isLoading.set(true);
    this.error.set(null);

    try {
      await FirebaseRemoteConfig.fetchAndActivate();
      await this.loadConfigValues();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to refresh remote config';
      this.loggingService.logException(err, 'RemoteConfigService: Refresh failed');
      this.error.set(errorMessage);
    } finally {
      this.isLoading.set(false);
    }
  }

  async getString(key: string): Promise<string> {
    if (!this.platformService.isNativeMobile()) {
      return '';
    }

    try {
      const result = await FirebaseRemoteConfig.getString({ key });
      return result.value;
    } catch {
      return '';
    }
  }

  async getBoolean(key: string): Promise<boolean> {
    if (!this.platformService.isNativeMobile()) {
      return false;
    }

    try {
      const result = await FirebaseRemoteConfig.getBoolean({ key });
      return result.value;
    } catch {
      return false;
    }
  }

  private async loadConfigValues(): Promise<void> {
    const appConfigJson = await this.getString('AppConfig');

    if (!appConfigJson) {
      this.loggingService.logWarning('RemoteConfigService: AppConfig not found, using defaults');
      return;
    }

    try {
      const appConfig: AppConfigJson = JSON.parse(appConfigJson);

      const hostEnv = this.env.appSettings?.hostEnv || 'LOCAL';
      const configKey = HOST_ENV_TO_CONFIG_KEY[hostEnv] || 'development';

      const envConfig = appConfig[configKey];

      if (envConfig) {
        const newConfig = {
          min_app_version_ios: envConfig.min_app_version_ios || DEFAULT_CONFIG.min_app_version_ios,
          min_app_version_android: envConfig.min_app_version_android || DEFAULT_CONFIG.min_app_version_android,
          force_update_enabled: envConfig.force_update_enabled ?? DEFAULT_CONFIG.force_update_enabled,
          force_update_title: envConfig.force_update_title || DEFAULT_CONFIG.force_update_title,
          force_update_message: envConfig.force_update_message || DEFAULT_CONFIG.force_update_message,
          maintenance_mode: envConfig.maintenance_mode ?? DEFAULT_CONFIG.maintenance_mode,
          maintenance_message: envConfig.maintenance_message || DEFAULT_CONFIG.maintenance_message,
          maintenance_message_web: envConfig.maintenance_message_web || DEFAULT_CONFIG.maintenance_message_web,
          ota_updates_enabled: envConfig.ota_updates_enabled ?? DEFAULT_CONFIG.ota_updates_enabled,
          ota_manifest_url: envConfig.ota_manifest_url || DEFAULT_CONFIG.ota_manifest_url,
          use_apim_customer_identity: envConfig.use_apim_customer_identity ?? DEFAULT_CONFIG.use_apim_customer_identity,
          show_origination: envConfig.show_origination ?? DEFAULT_CONFIG.show_origination,
          cookie_consent: {
            enabled: envConfig.cookie_consent?.enabled ?? DEFAULT_CONFIG.cookie_consent.enabled,
            frequency_unit: envConfig.cookie_consent?.frequency_unit || null,
            frequency_value: envConfig.cookie_consent?.frequency_value ?? null,
          },
        };
        this.config.set(newConfig);
      } else {
        this.loggingService.logWarning(`RemoteConfigService: No config found for environment: ${configKey}, using defaults`);
      }
    } catch (err) {
      this.loggingService.logException(err, 'RemoteConfigService: Failed to parse AppConfig JSON');
    }
  }
}
